import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';

const BALLOON_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];
const FIREWORK_COLORS = ['#fde047', '#f472b6', '#60a5fa', '#4ade80', '#fb923c', '#a78bfa'];

const seenKey = (date) => `birthdaySeen_${date}`;

/**
 * BirthdayCelebration — zobrazí všem uživatelům oslavný modal, když má někdo v týmu
 * narozeniny. Kolem modalu létají balonky a vybuchuje ohňostroj. Zobrazí se jednou denně.
 */
export default function BirthdayCelebration({ currentUserId, isDarkMode }) {
  const [celebrants, setCelebrants] = useState([]);
  const [date, setDate] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/birthdays/today?userId=${currentUserId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data.celebrants) && data.celebrants.length > 0) {
          const already = (() => { try { return localStorage.getItem(seenKey(data.date)); } catch (_) { return null; } })();
          setCelebrants(data.celebrants);
          setDate(data.date);
          if (!already) setVisible(true);
        }
      } catch (_) { /* tiše ignoruj */ }
    })();
    return () => { cancelled = true; };
  }, [currentUserId]);

  const close = () => {
    try { if (date) localStorage.setItem(seenKey(date), '1'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  if (!visible || celebrants.length === 0) return null;

  const names = celebrants.map(c => c.name);
  const namesText = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} a ${names[names.length - 1]}`;
  const verb = names.length === 1 ? 'má' : 'mají';

  const balloons = Array.from({ length: 14 });
  const fireworks = Array.from({ length: 8 });

  const overlay = (
    <div className="fixed inset-0 z-[11000] flex items-center justify-center overflow-hidden" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <style>{`
        @keyframes bc-float {
          0% { transform: translateY(110vh) rotate(-6deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(-20vh) rotate(6deg); opacity: 1; }
        }
        @keyframes bc-sway { 0%,100% { margin-left: -14px; } 50% { margin-left: 14px; } }
        @keyframes bc-burst {
          0% { transform: scale(0.1); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: scale(1); opacity: 0; }
        }
        @keyframes bc-pop {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .bc-balloon-wrap { position: absolute; bottom: 0; animation: bc-float linear infinite; }
        .bc-balloon-sway { animation: bc-sway ease-in-out infinite; }
        .bc-firework { position: absolute; width: 6px; height: 6px; border-radius: 9999px; }
        .bc-firework span {
          position: absolute; left: 0; top: 0; width: 4px; height: 4px; border-radius: 9999px;
          animation: bc-burst 1.4s ease-out infinite;
        }
      `}</style>

      {/* Balonky */}
      {balloons.map((_, i) => {
        const color = BALLOON_COLORS[i % BALLOON_COLORS.length];
        const left = (i * 7 + (i % 3) * 4) % 96;
        const dur = 7 + (i % 5);
        const delay = (i % 7) * 0.8;
        const swayDur = 2 + (i % 3);
        return (
          <div key={`b${i}`} className="bc-balloon-wrap" style={{ left: `${left}%`, animationDuration: `${dur}s`, animationDelay: `${delay}s` }}>
            <div className="bc-balloon-sway" style={{ animationDuration: `${swayDur}s` }}>
              <div style={{ width: 34, height: 44, background: color, borderRadius: '50% 50% 48% 48%', boxShadow: 'inset -6px -6px 0 rgba(0,0,0,0.08)' }} />
              <div style={{ width: 1, height: 60, background: 'rgba(255,255,255,0.5)', margin: '0 auto' }} />
            </div>
          </div>
        );
      })}

      {/* Ohňostroj kolem modalu */}
      {fireworks.map((_, i) => {
        const top = 15 + (i * 37) % 70;
        const left = 12 + (i * 53) % 76;
        const color = FIREWORK_COLORS[i % FIREWORK_COLORS.length];
        const delay = (i % 8) * 0.35;
        const particles = Array.from({ length: 12 });
        return (
          <div key={`f${i}`} className="bc-firework" style={{ top: `${top}%`, left: `${left}%` }}>
            {particles.map((__, j) => {
              const angle = (j / particles.length) * Math.PI * 2;
              const dist = 46;
              const x = Math.cos(angle) * dist;
              const y = Math.sin(angle) * dist;
              return (
                <span key={j} style={{ background: color, animationDelay: `${delay}s`, transform: `translate(${x}px, ${y}px)` }} />
              );
            })}
          </div>
        );
      })}

      {/* Modal */}
      <div
        className={`relative rounded-2xl shadow-2xl px-8 py-8 text-center max-w-md mx-4 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
        style={{ animation: 'bc-pop 0.5s ease-out' }}
      >
        <div className="text-6xl mb-3">🎂🎉</div>
        <h2 className="text-2xl font-extrabold mb-2">
          {namesText} {verb} dnes narozeniny!
        </h2>
        <p className="text-lg font-semibold text-pink-600 dark:text-pink-300 mb-4">Všechno nejlepší! 🥳🎈</p>
        {celebrants.some(c => c.age) && (
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {celebrants.filter(c => c.age).map(c => `${c.name} slaví ${c.age}`).join(' · ')}
          </p>
        )}
        <button
          onClick={close}
          className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold shadow-lg transition"
        >
          🎈 Popřát a zavřít
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}
